/**
 * Compliance Event Service
 * 
 * Manages individual protocol compliance events.
 * Handles CRUD operations for compliance event tracking.
 * 
 * Phase 6: Protocol Compliance Tracking
 */

import { db } from '../firebase';
import { collection, doc, addDoc, getDoc, getDocs, updateDoc, deleteDoc, query, where, orderBy, limit, Timestamp } from 'firebase/firestore';

export class ComplianceEventService {
  constructor() {
    this.collectionName = 'protocol_compliance_events';
  }

  /**
   * Create a new compliance event
   * 
   * @param {Object} eventData - The compliance event data
   * @param {string} userId - The user creating the event
   * @returns {Promise<string>} The created event ID
   */
  async createEvent(eventData, userId) {
    try {
      const event = {
        // Event Identification
        eventType: eventData.eventType, // "hand_hygiene" | "environmental_cleaning" | "ppe_usage" | "isolation_protocol" | "antimicrobial_stewardship"
        eventSubType: eventData.eventSubType, // "before_patient_contact" | "after_patient_contact" | "antibiotic_selection" | "duration_compliance" | etc.
        
        // Location Hierarchy (GDPR: No personal identifiers)
        location: {
          facilityId: eventData.location.facilityId || 'facility_001',
          floorId: eventData.location.floorId,
          departmentId: eventData.location.departmentId,
          roomId: eventData.location.roomId || null,
          bedId: eventData.location.bedId || null
        },
        
        // Personnel (GDPR: Encrypted/anonymized)
        personnel: {
          userId: this.hashUserId(eventData.personnel.userId), // Encrypted user ID
          role: eventData.personnel.role, // "nurse" | "doctor" | "technician" | "cleaning_staff"
          departmentId: eventData.personnel.departmentId,
          shiftId: eventData.personnel.shiftId || 'current_shift'
        },
        
        // Protocol Details
        protocol: {
          protocolId: eventData.protocol.protocolId,
          protocolName: eventData.protocol.protocolName,
          requiredActions: eventData.protocol.requiredActions || [],
          complianceStatus: eventData.protocol.complianceStatus, // "compliant" | "non_compliant" | "partial_compliant"
          complianceScore: eventData.protocol.complianceScore || 0 // 0-1 scale
        },
        
        // Event Details
        eventDetails: {
          timestamp: Timestamp.now(),
          duration: eventData.eventDetails.duration || 0, // seconds
          method: eventData.eventDetails.method || 'manual_entry', // "manual_entry" | "sensor_detected" | "rfid_tracked" | "audit_observed"
          notes: eventData.eventDetails.notes || '',
          equipmentUsed: eventData.eventDetails.equipmentUsed || [],
          patientContact: eventData.eventDetails.patientContact || false
        },
        
        // Compliance Metrics
        complianceMetrics: {
          actionsCompleted: eventData.complianceMetrics.actionsCompleted || 0,
          actionsRequired: eventData.complianceMetrics.actionsRequired || 0,
          timeToComplete: eventData.complianceMetrics.timeToComplete || 0, // seconds
          qualityScore: eventData.complianceMetrics.qualityScore || 0, // 0-1 scale
          deviations: eventData.complianceMetrics.deviations || []
        },
        
        // Metadata
        metadata: {
          createdAt: Timestamp.now(),
          createdBy: userId,
          dataSource: eventData.eventDetails.method || 'manual_entry',
          version: '1.0',
          isActive: true
        }
      };

      const docRef = await addDoc(collection(db, this.collectionName), event);
      console.log('✅ Compliance event created:', docRef.id);
      
      // Trigger aggregation update (async, don't wait)
      this.triggerAggregationUpdate(event).catch(err => {
        console.error('Error triggering aggregation update:', err);
      });
      
      return docRef.id;
    } catch (error) {
      console.error('Error creating compliance event:', error);
      throw error;
    }
  }

  /**
   * Get a compliance event by ID
   * 
   * @param {string} eventId - The event ID
   * @returns {Promise<Object>} The event data
   */
  async getEvent(eventId) {
    try {
      const docRef = doc(db, this.collectionName, eventId);
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        return { id: docSnap.id, ...docSnap.data() };
      } else {
        throw new Error('Event not found');
      }
    } catch (error) {
      console.error('Error getting compliance event:', error);
      throw error;
    }
  }

  /**
   * Get compliance events by location
   * 
   * @param {Object} locationFilter - Location filter (departmentId, roomId, etc.)
   * @param {Object} options - Query options (limit, orderBy, etc.)
   * @returns {Promise<Array>} Array of events
   */
  async getEventsByLocation(locationFilter, options = {}) {
    try {
      let q = collection(db, this.collectionName);
      
      // Apply location filters
      if (locationFilter.departmentId) {
        q = query(q, where('location.departmentId', '==', locationFilter.departmentId));
      }
      if (locationFilter.roomId) {
        q = query(q, where('location.roomId', '==', locationFilter.roomId));
      }
      if (locationFilter.floorId) {
        q = query(q, where('location.floorId', '==', locationFilter.floorId));
      }
      
      // Apply time filter
      if (options.startTime) {
        q = query(q, where('eventDetails.timestamp', '>=', options.startTime));
      }
      if (options.endTime) {
        q = query(q, where('eventDetails.timestamp', '<=', options.endTime));
      }
      
      // Apply ordering
      q = query(q, orderBy('eventDetails.timestamp', 'desc'));
      
      // Apply limit
      if (options.limit) {
        q = query(q, limit(options.limit));
      }
      
      const snapshot = await getDocs(q);
      return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    } catch (error) {
      console.error('Error getting events by location:', error);
      throw error;
    }
  }

  /**
   * Get compliance events by protocol
   * 
   * @param {string} protocolId - The protocol ID
   * @param {Object} options - Query options
   * @returns {Promise<Array>} Array of events
   */
  async getEventsByProtocol(protocolId, options = {}) {
    try {
      let q = query(
        collection(db, this.collectionName),
        where('protocol.protocolId', '==', protocolId)
      );
      
      // Apply time filter
      if (options.startTime) {
        q = query(q, where('eventDetails.timestamp', '>=', options.startTime));
      }
      if (options.endTime) {
        q = query(q, where('eventDetails.timestamp', '<=', options.endTime));
      }
      
      // Apply ordering
      q = query(q, orderBy('eventDetails.timestamp', 'desc'));
      
      // Apply limit
      if (options.limit) {
        q = query(q, limit(options.limit));
      }
      
      const snapshot = await getDocs(q);
      return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    } catch (error) {
      console.error('Error getting events by protocol:', error);
      throw error;
    }
  }

  /**
   * Get compliance events by personnel
   * 
   * @param {string} userId - The user ID (will be hashed)
   * @param {Object} options - Query options
   * @returns {Promise<Array>} Array of events
   */
  async getEventsByPersonnel(userId, options = {}) {
    try {
      const hashedUserId = this.hashUserId(userId);
      
      let q = query(
        collection(db, this.collectionName),
        where('personnel.userId', '==', hashedUserId)
      );
      
      // Apply time filter
      if (options.startTime) {
        q = query(q, where('eventDetails.timestamp', '>=', options.startTime));
      }
      if (options.endTime) {
        q = query(q, where('eventDetails.timestamp', '<=', options.endTime));
      }
      
      // Apply ordering
      q = query(q, orderBy('eventDetails.timestamp', 'desc'));
      
      // Apply limit
      if (options.limit) {
        q = query(q, limit(options.limit));
      }
      
      const snapshot = await getDocs(q);
      return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    } catch (error) {
      console.error('Error getting events by personnel:', error);
      throw error;
    }
  }

  /**
   * Update a compliance event
   * 
   * @param {string} eventId - The event ID
   * @param {Object} updates - The updates to apply
   * @param {string} userId - The user making the update
   * @returns {Promise<void>}
   */
  async updateEvent(eventId, updates, userId) {
    try {
      const docRef = doc(db, this.collectionName, eventId);
      
      const updateData = {
        ...updates,
        'metadata.updatedAt': Timestamp.now(),
        'metadata.updatedBy': userId
      };
      
      await updateDoc(docRef, updateData);
      console.log('✅ Compliance event updated:', eventId);
    } catch (error) {
      console.error('Error updating compliance event:', error);
      throw error;
    }
  }

  /**
   * Delete a compliance event (soft delete)
   * 
   * @param {string} eventId - The event ID
   * @param {string} userId - The user deleting the event
   * @returns {Promise<void>}
   */
  async deleteEvent(eventId, userId) {
    try {
      const docRef = doc(db, this.collectionName, eventId);
      
      await updateDoc(docRef, {
        'metadata.isActive': false,
        'metadata.deletedAt': Timestamp.now(),
        'metadata.deletedBy': userId
      });
      
      console.log('✅ Compliance event deleted (soft):', eventId);
    } catch (error) {
      console.error('Error deleting compliance event:', error);
      throw error;
    }
  }

  /**
   * Hash user ID for GDPR compliance
   * Simple hash for now, should use proper encryption in production
   * 
   * @param {string} userId - The user ID to hash
   * @returns {string} The hashed user ID
   */
  hashUserId(userId) {
    // TODO: Implement proper encryption/hashing
    // For now, just prefix with 'hashed_' for demonstration
    return `hashed_${userId}`;
  }

  /**
   * After a new compliance event is created, auto-update `implementationStatus`
   * on every standard_requirement linked to the executed protocol.
   *
   * Thresholds mirror StandardDetail.jsx:
   *   rate >= 0.80 → 'Implemented'
   *   rate >  0    → 'At Risk'
   *   rate == 0    → 'In Progress'
   *
   * @param {Object} event - The compliance event just written
   * @returns {Promise<void>}
   */
  async triggerAggregationUpdate(event) {
    const protocolId = event?.protocol?.protocolId;
    if (!protocolId) return;

    try {
      // Load the protocol document to get linkedRequirements
      const protocolRef = doc(db, 'protocols', protocolId);
      const protocolSnap = await getDoc(protocolRef);
      if (!protocolSnap.exists()) return;

      const linkedRequirements = protocolSnap.data()?.linkedRequirements;
      if (!Array.isArray(linkedRequirements) || linkedRequirements.length === 0) return;

      // Compute fresh 30-day rate for this protocol
      const { rate } = await this.getProtocol30DayComplianceRate(protocolId);

      const newStatus =
        rate >= 0.8 ? 'Implemented' : rate > 0 ? 'At Risk' : 'In Progress';

      // Dynamically import to avoid circular dependency
      const { default: qmsStandardService } = await import('./qmsStandardService');

      await Promise.all(
        linkedRequirements.map(async (link) => {
          const srId = link.standardRequirementId;
          if (!srId) return;
          try {
            await qmsStandardService.updateStandardRequirement(
              srId,
              { implementationStatus: newStatus },
              event?.metadata?.createdBy || 'system',
              'System (compliance auto-sync)'
            );
          } catch (e) {
            console.warn(`[ComplianceEventService] Could not update SR ${srId}:`, e?.message);
          }
        })
      );

      console.debug(`[ComplianceEventService] Auto-synced ${linkedRequirements.length} standard_requirements for protocol ${protocolId} → ${newStatus} (rate: ${Math.round(rate * 100)}%)`);
    } catch (error) {
      console.warn('[ComplianceEventService] triggerAggregationUpdate failed (non-blocking):', error?.message);
    }
  }

  /**
   * Get 30-day compliance rate for a protocol (Option B: from protocol_compliance_events).
   * @param {string} protocolId - Protocol Firestore document id
   * @returns {Promise<{ rate: number, totalEvents: number, lastExecution: Firestore.Timestamp|null }>}
   */
  async getProtocol30DayComplianceRate(protocolId) {
    if (!protocolId) return { rate: 0, totalEvents: 0, lastExecution: null };
    try {
      const end = new Date();
      const start = new Date(end);
      start.setDate(start.getDate() - 30);
      const startTime = Timestamp.fromDate(start);
      const endTime = Timestamp.fromDate(end);
      const events = await this.getEventsByProtocol(protocolId, {
        startTime,
        endTime,
        limit: 5000,
      });
      const total = events.length;
      const compliant = events.filter((e) => e.protocol?.complianceStatus === 'compliant').length;
      const rate = total > 0 ? compliant / total : 0;
      const lastEvent = events.length ? events[0] : null;
      const lastExecution = lastEvent?.eventDetails?.timestamp ?? null;
      return {
        rate: Math.round(rate * 1000) / 1000,
        totalEvents: total,
        lastExecution,
      };
    } catch (err) {
      console.error('getProtocol30DayComplianceRate:', err);
      return { rate: 0, totalEvents: 0, lastExecution: null };
    }
  }

  /**
   * Get compliance statistics for a location
   * 
   * @param {Object} locationFilter - Location filter
   * @param {Object} timeRange - Time range (startTime, endTime)
   * @returns {Promise<Object>} Compliance statistics
   */
  async getComplianceStatistics(locationFilter, timeRange) {
    try {
      const events = await this.getEventsByLocation(locationFilter, timeRange);
      
      const totalEvents = events.length;
      const compliantEvents = events.filter(e => e.protocol.complianceStatus === 'compliant').length;
      const nonCompliantEvents = events.filter(e => e.protocol.complianceStatus === 'non_compliant').length;
      const partialCompliantEvents = events.filter(e => e.protocol.complianceStatus === 'partial_compliant').length;
      
      const overallComplianceRate = totalEvents > 0 ? compliantEvents / totalEvents : 0;
      const averageComplianceScore = totalEvents > 0 
        ? events.reduce((sum, e) => sum + e.protocol.complianceScore, 0) / totalEvents 
        : 0;
      
      return {
        totalEvents,
        compliantEvents,
        nonCompliantEvents,
        partialCompliantEvents,
        overallComplianceRate,
        averageComplianceScore,
        timeRange
      };
    } catch (error) {
      console.error('Error getting compliance statistics:', error);
      throw error;
    }
  }
}

export const complianceEventService = new ComplianceEventService();
